import { types, flow } from 'mobx-state-tree';
import { UIStore } from './UIStore.js';
import { AuthStore } from './AuthStore.js';
import { NotificationStore } from './NotificationStore.js';
import { MSTModelStore } from './MSTModelStore.js';
import apiClient from '../../src/services/apiClient.js';
import socketService from '../../src/services/socketService.js';
import { initOperationHandler } from '../../src/services/OperationHandler.js';

/**
 * Корневой MST стор - единая точка входа для всего состояния приложения
 * Объединяет UI, Auth, бизнес-модели и обеспечивает их взаимодействие
 */
const RootStore = types
    .model('RootStore', {
        // Все дочерние сторы на MST
        ui: types.optional(UIStore, {}),
        auth: types.optional(AuthStore, {}),
        notifications: types.optional(NotificationStore, {}),
        models: types.optional(MSTModelStore, {}), // Теперь MST модель!

        // Централизованное хранилище тегов
        tags: types.frozen([]),
        tagsLoading: types.optional(types.boolean, false),
    })
    .volatile(() => ({
        // Volatile для сервисов и не-сериализуемых данных
        apiClient: apiClient,
        socketService: socketService,
        eventHandlers: new Map(),
        isInitialized: false,
    }))
    .views(self => ({
        // Глобальные вычисляемые значения
        get isReady() {
            // Приложение готово когда загружены модели и проверена сессия
            return self.isInitialized && !self.auth.isLoading;
        },
        
        get currentUser() {
            return self.auth.user;
        },
        
        get isAuthenticated() {
            return self.auth.isAuthenticated;
        },
        
        // Универсальный доступ к коллекциям
        getCollection(name) {
            return self.models.getCollectionManager(name);
        }
    }))
    .actions(self => ({
        // Действия для работы с тегами
        loadTags: flow(function* () {
            if (self.tagsLoading) return;

            try {
                self.tagsLoading = true;
                const tags = yield self.apiClient.get('/api/tags');
                self.tags = Array.isArray(tags) ? tags : [];
            } catch (error) {
                console.error('Failed to load tags:', error);
                self.tags = [];
            } finally {
                self.tagsLoading = false;
            }
        }),

        setTags(tags) {
            self.tags = Array.isArray(tags) ? tags : [];
        },

        addTag: flow(function* (tag) {
            try {
                yield self.apiClient.post('/api/tags', { tags: [tag] });
                // WebSocket обновит список автоматически
            } catch (error) {
                console.error('Failed to add tag:', error);
                throw error;
            }
        }),

        removeTag: flow(function* (tag) {
            try {
                yield self.apiClient.delete(`/api/tags/${encodeURIComponent(tag)}`);
                // WebSocket обновит список автоматически
            } catch (error) {
                console.error('Failed to remove tag:', error);
                throw error;
            }
        }),

        // Инициализация WebSocket обработчиков
        initWebSocket() {
            // Базовые события подключения
            self.socketService.on('connect', () => {
                console.log('WebSocket connected');
                // Не показываем уведомление, это техническое событие
            });

            self.socketService.on('disconnect', () => {
                console.log('WebSocket disconnected');
                // Можно обработать в UI через состояние isOnline
            });

            self.socketService.on('error', (error) => {
                console.error('WebSocket error:', error);
                // Ошибки логируем, но не показываем пользователю
            });

            // Подписка на обновления тегов
            self.socketService.on('tags:updated', (data) => {
                console.log('Tags updated via WebSocket:', data);
                // Загружаем теги через API при получении уведомления
                self.loadTags();
            });
        },
        
        // Регистрация обработчика события от дочернего стора
        registerEventHandler(eventName, handler, storeName) {
            console.log(`[RootStore] Registering handler for event: ${eventName} from ${storeName}`);
            if (!self.eventHandlers.has(eventName)) {
                self.eventHandlers.set(eventName, new Map());

                // Подписываемся на событие в socket только один раз
                self.socketService.on(eventName, (data) => {
                    console.log(`[RootStore] WebSocket event received: ${eventName}`, data);
                    // Вызываем все зарегистрированные обработчики
                    const handlers = self.eventHandlers.get(eventName);
                    if (handlers) {
                        handlers.forEach((handler, store) => {
                            try {
                                handler(data);
                            } catch (error) {
                                console.error(`Error in ${store} handler for ${eventName}:`, error);
                            }
                        });
                    }
                });
            }

            self.eventHandlers.get(eventName).set(storeName, handler);

            // Возвращаем функцию отписки
            return () => {
                self.unregisterEventHandler(eventName, storeName);
            };
        },
        
        // Отмена регистрации обработчика
        unregisterEventHandler(eventName, storeName) {
            const handlers = self.eventHandlers.get(eventName);
            if (handlers) {
                handlers.delete(storeName);
                
                // Если больше нет обработчиков, отписываемся от события
                if (handlers.size === 0) {
                    self.socketService.off(eventName);
                    self.eventHandlers.delete(eventName);
                }
            }
        },
        
        // Инициализация приложения
        initialize: flow(function* () {
            console.log('Initializing RootStore...');
            
            try {
                // Инициализируем OperationHandler
                initOperationHandler(self);
                console.log('OperationHandler initialized');
                
                // СНАЧАЛА подключаем WebSocket
                self.socketService.connect();
                self.initWebSocket();
                
                // Проверяем сессию (AuthStore автоматически загрузит модели если авторизован)
                const isAuthenticated = yield self.auth.checkSession();

                if (isAuthenticated) {
                    console.log('User authenticated, models loaded');
                    // Загружаем теги после успешной авторизации
                    yield self.loadTags();
                } else {
                    console.log('User not authenticated');
                }
                
                self.isInitialized = true;
                
            } catch (error) {
                console.error('Failed to initialize app:', error);
                throw error;
            }
        }),
        
        // Глобальный сброс всех сторов
        reset() {
            console.log('Resetting all stores');
            
            // Сброс дочерних сторов
            self.ui.reset();
            self.auth.reset();
            self.notifications.clear();
            if (self.modelsStore) {
                self.modelsStore.reset();
            }
            
            // Очищаем все обработчики событий
            self.eventHandlers.forEach((handlers, eventName) => {
                self.socketService.off(eventName);
            });
            self.eventHandlers.clear();
            
            // Отключаем WebSocket
            self.socketService.disconnect();

            // Сбрасываем теги
            self.tags = [];
            self.tagsLoading = false;

            self.isInitialized = false;
        },
        
        // Lifecycle hooks
        afterCreate() {
            console.log('RootStore created');
            // Инициализация теперь вызывается явно из main.jsx
        }
    }));

export { RootStore };